import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { FaExclamationCircle } from 'react-icons/fa';
import { FaSpinner } from 'react-icons/fa';
import logo from '../../assets/images/transparent.png'; // or .png, .svg

export default function InvoiceComponent() {
    const { id } = useParams();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setIsSmallMobile(window.innerWidth <= 480);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        const fetchInvoice = async () => {
            setLoading(true);
            setError(null);
            try {
                const requestOptions = {
                    method: "GET",
                    redirect: 'follow'
                };
                const response = await fetch(`https://api.alburaqscents.com/v1/order/detail/${id}`, requestOptions);
                const result = await response.json();
                if (result.success) {
                    setInvoiceData(result.data);
                } else {
                    setError('Failed to fetch invoice data.');
                }
            } catch (err) {
                setError('An error occurred while fetching invoice data.');
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchInvoice();
        else setError('No invoice ID provided.');

        return () => window.removeEventListener('resize', handleResize);
    }, [id]);

    // Truncate long names
    const truncateName = (name, maxLength = 20) => {
        return name?.length > maxLength ? name.substring(0, maxLength) + '...' : name;
    };

    const styles = {
        mainWrapper: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            width: '100%',
            backgroundColor: 'transparent',
            padding: isSmallMobile ? '10px' : (isMobile ? '20px' : '40px'),
            boxSizing: 'border-box',
            fontFamily: 'Arial, sans-serif',
            overflow: 'auto',
        },
        invoiceContainer: {
            backgroundColor: '#000',
            borderRadius: '15px',
            width: '100%',
            maxWidth: '700px', // Increased for better layout
            boxShadow: '0 5px 15px rgba(0,0,0,0.5)',
            padding: isSmallMobile ? '20px' : '30px',
            boxSizing: 'border-box',
            color: '#E5D0A5',
            position: 'relative',
            minHeight: '400px',
        },
        checkmarkOverlay: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: isSmallMobile ? '80px' : '120px',
            color: '#4CAF50',
            zIndex: 1,
            opacity: 0.7,
            pointerEvents: 'none',
        },
        loader: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: isSmallMobile ? '40px' : '60px',
            color: '#D4AF37',
            animation: 'spin 1.5s ease-in-out infinite', // Improved spinning animation
            zIndex: 2,
        },
        errorMessage: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#FF4444',
            fontSize: isSmallMobile ? '16px' : '20px',
            textAlign: 'center',
            padding: '20px',
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            zIndex: 2,
        },
        topSection: {
            backgroundColor: '#1a1a1a',
            borderRadius: '10px',
            padding: isSmallMobile ? '15px' : '20px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? '15px' : '30px',
        },
        logoSection: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : 'flex-start',
        },
        logo: {
            width: isSmallMobile ? '50px' : '60px',
            height: isSmallMobile ? '50px' : '60px',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        logoImage: {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        },
        companyName: {
            fontSize: isSmallMobile ? '16px' : '18px',
            fontWeight: 'bold',
            color: '#E5D0A5',
            margin: 0,
            textAlign: isMobile ? 'center' : 'left',
            maxWidth: '150px', // Limit width to prevent overflow
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        addressSection: {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            fontSize: '12px',
            color: '#BBB',
            width: isMobile ? '100%' : 'auto',
            textAlign: isMobile ? 'center' : 'left',
        },
        addressText: {
            margin: 0,
            lineHeight: '1.4',
        },
        invoiceDetails: {
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            fontSize: '13px',
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto',
            alignSelf: isMobile ? 'center' : 'flex-start',
            textAlign: isMobile ? 'center' : 'right',
        },
        invoiceDetailRow: {
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'space-between',
            gap: '10px',
            whiteSpace: 'nowrap',
        },
        invoiceDetailLabel: {
            fontWeight: 'normal',
            color: '#BBB',
            minWidth: '80px',
            textAlign: 'left',
        },
        invoiceDetailValue: {
            fontWeight: 'bold',
            color: '#E5D0A5',
            textAlign: 'right',
            flexGrow: 1,
        },
        statusPill: {
            backgroundColor: '#4CAF50',
            color: '#000',
            padding: '3px 8px',
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: 'bold',
            display: 'inline-block',
            marginLeft: isMobile ? '0' : '5px',
        },
        statusPillPending: {
            backgroundColor: '#FF0000',
            color: '#FFF',
            padding: '3px 8px',
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: 'bold',
            display: 'inline-block',
            marginLeft: isMobile ? '0' : '5px',
        },
        issueSection: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '20px' : '30px',
            marginBottom: '20px',
            padding: '0 10px',
        },
        issueBlock: {
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            fontSize: '13px',
        },
        issueTitle: {
            color: '#BBB',
            fontWeight: 'bold',
            marginBottom: '5px',
        },
        issueName: {
            fontWeight: 'bold',
            color: '#E5D0A5',
            marginBottom: '3px',
        },
        issueAddress: {
            color: '#BBB',
            lineHeight: '1.4',
        },
        productTableWrapper: {
            marginBottom: '20px',
            padding: '0 10px',
            overflowX: 'auto',
            paddingBottom: '17px',
            marginBottom: '-17px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '::-webkit-scrollbar': { width: '0px', height: '0px', background: 'transparent' },
        },
        productTable: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: isSmallMobile ? '400px' : (isMobile ? '500px' : 'auto'),
        },
        productTableHeader: {
            backgroundColor: '#1a1a1a',
            color: '#E5D0A5',
            fontSize: '12px',
            textAlign: 'left',
            fontWeight: 'bold',
        },
        productTh: {
            padding: '12px 8px',
            whiteSpace: 'nowrap',
            borderBottom: '1px solid #333',
        },
        productRow: {
            borderBottom: '1px solid #333',
        },
        productTd: {
            padding: '10px 8px',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            fontSize: '12px',
            color: '#E5D0A5',
        },
        productIcon: {
            width: '20px',
            height: '20px',
            marginRight: '5px',
            verticalAlign: 'middle',
        },
        totalsSection: {
            padding: '0 10px',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '5px',
        },
        totalsRow: {
            display: 'flex',
            justifyContent: 'space-between',
            width: isSmallMobile ? '100%' : '60%',
            maxWidth: '250px',
            fontSize: '13px',
        },
        totalsLabel: {
            color: '#BBB',
        },
        totalsValue: {
            fontWeight: 'bold',
            color: '#E5D0A5',
        },
        totalsDivider: {
            width: isSmallMobile ? '100%' : '60%',
            maxWidth: '250px',
            borderBottom: '1px solid #333',
            margin: '5px 0',
        },
        grandTotalRow: {
            display: 'flex',
            justifyContent: 'space-between',
            width: isSmallMobile ? '100%' : '60%',
            maxWidth: '250px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#E5D0A5',
        },
        warningBox: {
            backgroundColor: '#B22222',
            borderRadius: '10px',
            padding: '15px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            marginBottom: '20px',
            fontSize: '12px',
            lineHeight: '1.4',
            color: '#fff',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            border: '1px solid #FF4444',
        },
        warningIcon: {
            fontSize: '15px',
            color: '#fff',
            flexShrink: 0,
            marginTop: '2px',
        },
        buttonsSection: {
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            padding: '0 10px',
        },
        button: {
            padding: '10px 25px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            flexGrow: 1,
            maxWidth: '150px',
        },
        printButton: {
            backgroundColor: '#D4AF37',
            color: '#000',
        },
        printButtonHover: {
            backgroundColor: '#C0A020',
        },
        submitButton: {
            backgroundColor: '#D4AF37',
            color: '#000',
        },
        submitButtonHover: {
            backgroundColor: '#C0A020',
        },
    };

    return (
        <div style={styles.mainWrapper}>
            <div style={styles.invoiceContainer}>
                {loading && <FaSpinner style={styles.loader} />}
                {error && <div style={styles.errorMessage}>{error}</div>}
                {!loading && !error && invoiceData && (
                    <>
                        {invoiceData.orderStatus === 'delivered' && <FaCheckCircle style={styles.checkmarkOverlay} />}

                        <div style={styles.topSection}>
                            <div style={styles.logoSection}>
                                <div style={styles.logo}>
                                    <img src={logo} alt="Logo" style={styles.logoImage} />
                                </div>
                                <h2 style={styles.companyName}>{truncateName(invoiceData?.customerId?.name)}</h2>
                            </div>

                            <div style={styles.invoiceDetails}>
                                <div style={styles.invoiceDetailRow}>
                                    <span style={styles.invoiceDetailLabel}>Invoice:</span>
                                    <span style={styles.invoiceDetailValue}>#{invoiceData.orderNumber}</span>
                                </div>
                                <div style={styles.invoiceDetailRow}>
                                    <span style={styles.invoiceDetailLabel}>Issue Date:</span>
                                    <span style={styles.invoiceDetailValue}>{new Date(invoiceData.createdAt).toLocaleDateString()}</span>
                                </div>
                                <div style={styles.invoiceDetailRow}>
                                    <span style={styles.invoiceDetailLabel}>Due Date:</span>
                                    <span style={styles.invoiceDetailValue}>{new Date(new Date(invoiceData.createdAt).setDate(new Date(invoiceData.createdAt).getDate() + 5)).toLocaleDateString()}</span>
                                </div>
                                <div style={styles.invoiceDetailRow}>
                                    <span style={styles.invoiceDetailLabel}>Amount:</span>
                                    <span style={styles.invoiceDetailValue}>Rs {invoiceData.totalAmount}</span>
                                </div>
                                <div style={styles.invoiceDetailRow}>
                                    <span style={styles.invoiceDetailLabel}>Status:</span>
                                    <span style={invoiceData.orderStatus === 'delivered' ? styles.statusPill : styles.statusPillPending}>
                                        {invoiceData.orderStatus === 'delivered' ? 'Paid' : 'Pending'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div style={styles.issueSection}>
                            <div style={styles.issueBlock}>
                                <p style={styles.issueTitle}>Issue From:</p>
                                <p style={styles.issueName}>Buraq Admin NIC</p>
                                <p style={styles.issueAddress}>Hassan Trade Center, Main Mianwali Road, 8 Block Sargodha</p>
                                <p style={styles.issueAddress}>Phone: {invoiceData.shippingAddress.phoneNumber}</p>
                                <p style={styles.issueAddress}>Email: alborakscents@gmail.com</p>
                            </div>
                            <div style={styles.issueBlock}>
                                <p style={styles.issueTitle}>Issue For:</p>
                                <p style={styles.issueName}>{invoiceData.customerId?.name || 'Unknown'}</p>
                                <p style={styles.issueAddress}>{invoiceData.shippingAddress.address}</p>
                                <p style={styles.issueAddress}>Phone: {invoiceData.shippingAddress.phoneNumber}</p>
                                <p style={styles.issueAddress}>Email: {invoiceData.customerId?.email || 'N/A'}</p>
                            </div>
                        </div>

                        <div style={styles.productTableWrapper}>
                            <table style={styles.productTable}>
                                <thead style={styles.productTableHeader}>
                                    <tr>
                                        <th style={styles.productTh}>Product Name</th>
                                        <th style={styles.productTh}>Quantity</th>
                                        <th style={styles.productTh}>Price</th>
                                        <th style={styles.productTh}>Tax</th>
                                        <th style={styles.productTh}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceData.items.map((item, index) => (
                                        <tr key={index} style={styles.productRow}>
                                            <td style={styles.productTd}>{index + 1}</td> {/* Order number column */}
                                            <td style={styles.productTd}>
                                                {item.productId.name}
                                            </td>
                                            <td style={styles.productTd}>{item.quantity}</td>
                                            <td style={styles.productTd}>{item.priceAtPurchase}</td>
                                            <td style={styles.productTd}>29</td>
                                            <td style={styles.productTd}>{item.priceAtPurchase + 29}</td>
                                        </tr>
                                    ))}

                                </tbody>
                            </table>
                        </div>

                        <div style={styles.totalsSection}>
                            <div style={styles.totalsRow}>
                                <span style={styles.totalsLabel}>Sub Total :</span>
                                <span style={styles.totalsValue}>{invoiceData.items.reduce((sum, item) => sum + item.priceAtPurchase, 0)}</span>
                            </div>
                            <div style={styles.totalsRow}>
                                <span style={styles.totalsLabel}>Discount :</span>
                                <span style={styles.totalsValue}>0</span>
                            </div>
                            <div style={styles.totalsRow}>
                                <span style={styles.totalsLabel}>Estimated Tax (15.5%) :</span>
                                <span style={styles.totalsValue}>{Math.round(invoiceData.totalAmount * 0.155)}</span>
                            </div>
                            <div style={styles.totalsDivider}></div>
                            <div style={styles.grandTotalRow}>
                                <span>Grand Amount :</span>
                                <span>{invoiceData.totalAmount}</span>
                            </div>
                        </div>

                        <div style={styles.warningBox}>
                            <FaExclamationCircle style={styles.warningIcon} />
                            <p style={{ margin: 0 }}>
                                All accounts are to be paid within 7 days from receipt of invoice. To be paid by cheque or credit card or direct payment online. If account is not paid within 7 days, 5% credit's daily supplied as confirmation of work undertaken will be charged the agreed quoted fee mentioned above.
                            </p>
                        </div>

                        <div style={styles.buttonsSection}>
                            <button
                                style={{ ...styles.button, ...styles.printButton }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.printButtonHover.backgroundColor}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.printButton.backgroundColor}
                            >
                                Print
                            </button>
                            <button
                                style={{ ...styles.button, ...styles.submitButton }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor}
                            >
                                Submit
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}